const { logError, logWarn } = require('./utils');

// Max retry count and backoff factor for window.ua
const UA_RETRY_MAX_COUNT = 5;
const UA_RETRY_BACKOFF_FACTOR = 2;
const UA_RETRY_AFTER_MS = 60;

/**
 * Call window.ua
 *
 * @param {string} methodName
 * @param  {...any} args
 */
function uaCall(methodName, ...args) {
    uaCallWithRetry(methodName, args, UA_RETRY_MAX_COUNT);
}

/**
 * Call window.ua and return a Promise
 *
 * @param {string} methodName
 * @param  {...any} args
 * @returns {Promise}
 */
function uaCallWithReturnValue(methodName, ...args) {
    return uaCallWithReturnValueWithRetry(methodName, args, UA_RETRY_MAX_COUNT);
}

function uaCallWithReturnValueWithRetry(methodName, args, maxRetries) {
    return new Promise((resolve, reject) => {
        const fn = retriesLeft => {
            const ua = getRealUA();
            const backoffExp = UA_RETRY_BACKOFF_FACTOR ** (UA_RETRY_MAX_COUNT - retriesLeft + 1);

            if (typeof ua !== 'undefined' && retriesLeft > 0) {
                try {
                    const returnValue = ua(methodName, ...args);
                    // undefined means that the SDK has loaded but has not initialized yet
                    if (typeof returnValue === 'undefined') {
                        setTimeout(() => {
                            fn(retriesLeft - 1);
                        }, backoffExp * UA_RETRY_AFTER_MS);
                    } else {
                        if (returnValue.error) {
                            reject(new Error(returnValue.msg));
                        }

                        resolve(returnValue.value);
                    }
                } catch (e) {
                    reject(e);
                }
            } else if (retriesLeft > 0) {
                setTimeout(() => {
                    fn(retriesLeft - 1);
                }, backoffExp * UA_RETRY_AFTER_MS);
            } else {
                reject(
                    new Error(`window.ua was never initialized. The ${methodName} call failed.`)
                );
            }
        };

        fn(maxRetries);
    });
}

/**
 * Call window.ua with retry
 *
 * @param {string} methodName
 * @param {any[]} args
 * @param {Number} currentRetryNum
 */
function uaCallWithRetry(methodName, args, currentRetryNum) {
    const ua = getUA();

    if (typeof ua !== 'undefined') {
        try {
            ua(methodName, ...args);
            return;
        } catch (err) {
            logError(`Tried to call ua.${methodName}, but there was an error`);
        }
    }
    if (currentRetryNum > 0) {
        const backoffExp = UA_RETRY_BACKOFF_FACTOR ** (UA_RETRY_MAX_COUNT - currentRetryNum + 1);
        setTimeout(() => {
            uaCallWithRetry(methodName, args, currentRetryNum - 1);
        }, backoffExp * UA_RETRY_AFTER_MS);
    } else {
        logWarn(`A call was dropped to UA: ${methodName} - ${args}`);
    }
}

/**
 * Call window.ua('datalayer', ...)
 *
 * @param {string} methodName
 * @param  {...any} args
 * @returns {Promise}
 */
function uaDataLayerCall(methodName, ...args) {
    return uaDataLayerCallWithRetry(methodName, args, UA_RETRY_MAX_COUNT);
}

/**
 * Call window.ua('datalayer', ...) with retry
 *
 * @param {string} methodName
 * @param {any[]} args
 * @param {Number} maxRetries
 * @returns {Promise}
 */
function uaDataLayerCallWithRetry(methodName, args, maxRetries) {
    return new Promise((resolve, reject) => {
        const fn = retriesLeft => {
            const ua = getRealUA();

            if (typeof ua !== 'undefined') {
                ua('datalayer', methodName, ...args)
                    .then(rtn => {
                        resolve(rtn);
                    })
                    .catch(e => {
                        reject(e);
                    });
            } else if (retriesLeft > 0) {
                const backoffExp =
                    UA_RETRY_BACKOFF_FACTOR ** (UA_RETRY_MAX_COUNT - retriesLeft + 1);
                setTimeout(() => {
                    fn(retriesLeft - 1);
                }, backoffExp * UA_RETRY_AFTER_MS);
            } else {
                reject(
                    new Error(
                        `window.ua data layer never initialized. The "${methodName}" call failed.`
                    )
                );
            }
        };

        fn(maxRetries);
    });
}

/**
 * Get the window.ua object
 *
 * @returns {window.ua|undefined}
 */
function getUA() {
    return typeof window !== 'undefined' && window.ua ? window.ua : undefined;
}

/**
 * Get the real window.ua object
 * - Will not return if window.ua is only stubbed
 *
 * @returns {window.ua|undefined}
 */
function getRealUA() {
    return typeof window !== 'undefined' &&
        typeof window.ua !== 'undefined' &&
        window.ua.initialized === true
        ? window.ua
        : undefined;
}

module.exports = {
    uaCall,
    uaDataLayerCall,
    getUA,
    getRealUA,
    uaCallWithReturnValue,
};
